import { TooltipText } from "@/components/ToolTip";
import { cn } from "@/lib/utils";
import { StaffStatus, StaffType } from "@/services/staff.service";
import { createColumnHelper } from "@tanstack/react-table";
import { Eye, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ActionDropdown, ActionDropdownContent, ActionDropdownTrigger, DropdownNode } from "@/components/ActionDropdown";

const columnHelper = createColumnHelper<StaffType>();

export type StaffActionType = {
  id: string;
  name: string;
  action: "view" | "edit" | "delete";
};

// ─── Status pill config ───────────────────────────────────────────────────────

const STATUS_CONFIG: Record<StaffStatus, { label: string; className: string }> =
  {
    active: {
      label: "Active",
      className: "bg-gray-900 text-white",
    },
    inactive: {
      label: "Inactive",
      className: "bg-gray-100 text-gray-500",
    },
    on_leave: {
      label: "On Leave",
      className: "bg-gray-100 text-gray-600",
    },
  };

export const createStaffColumns = (
  onActionClick: (payload: StaffActionType) => void
) => [
  // Name — link
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <Link
        to={`/people/staff/${info.row.original.id}`}
        className="font-medium hover:underline"
      >
        {info.getValue()}
      </Link>
    ),
  }),

  // Email — tooltip on long addresses
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => (
      <TooltipText text={info.getValue()} maxChars={30} className="text-sm" />
    ),
  }),

  // Department — bordered pill (matching image)
  // columnHelper.accessor("department_id", {
  //   header: "Department",
  //   cell: (info) => (
  //     <span className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium">
  //       {info.getValue()}
  //     </span>
  //   ),
  // }),

  // Designation — plain text
  columnHelper.accessor("designation_name", {
    header: "Designation",
    cell: (info) => <span className="text-sm">{info.getValue() || "-"}</span>,
  }),

  // Subjects — tooltip when many subjects
  // columnHelper.accessor("subjects", {
  //   header: "Subjects",
  //   cell: (info) => {
  //     const subjects = info.getValue() ?? [];
  //     const text = subjects.join(", ");
  //     return <TooltipText text={text} maxChars={40} className="text-sm" />;
  //   },
  // }),

  // Status pill
  columnHelper.accessor("is_active", {
    header: "Status",
    cell: (info) => {
      const isActive = info.getValue() ?? true;
      const cfg = isActive ? STATUS_CONFIG.active : STATUS_CONFIG.inactive;
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "",
    cell: (info) => {
      const data = info.row.original;

      const dropdownNodes: DropdownNode[] = [
        {
          type: "group",
          children: [
            {
              type: "item",
              label: "View Details",
              icon: <Eye size={14} />,
              onSelect: () => onActionClick({ id: data.id, name: data.name, action: "view" }),
            },
            {
              type: "item",
              label: "Edit Staff",
              icon: <Pencil size={14} />,
              onSelect: () => onActionClick({ id: data.id, name: data.name, action: "edit" }),
            },
          ],
        },
        { type: "separator" },
        {
          type: "item",
          label: "Delete Staff",
          icon: <Trash2 size={14} />,
          className: "text-red-600 focus:text-red-600",
          iconClass: "text-red-600",
          onSelect: () => onActionClick({ id: data.id, name: data.name, action: "delete" }),
        },
      ];

      return (
        <ActionDropdown>
          <ActionDropdownTrigger
            triggerContent={<MoreVertical size={16} />}
            buttonVariant="ghost"
            buttonSize="icon"
            className={{ buttonClass: "h-8 w-8 p-0" }}
          />
          <ActionDropdownContent dropdownShape={dropdownNodes} align="end" />
        </ActionDropdown>
      );
    },
  }),
];
